import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { openTrialPoster } from "../lib/trialPoster";
import {
  Building2, Plus, Trash2, Pencil, Check, X, Users, ChevronDown, ChevronRight,
  Link2, Copy, UserPlus, ShieldCheck, Loader2, Crown, Send, Upload, ClipboardList, LineChart, Ticket, RefreshCw, TrendingUp, DollarSign, Clock, QrCode,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errMsg = (e, fb) => e?.response?.data?.detail || fb;

// Parse pasted/CSV roster text -> [{name, email}] (dedup by email; skips header/invalid lines)
function parseRoster(text) {
  const seen = new Set();
  const rows = [];
  (text || "").split(/\r?\n/).forEach((line) => {
    const parts = line.split(/[,;\t]/).map((p) => p.trim()).filter(Boolean);
    const email = parts.find((p) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p));
    if (!email) return;
    const key = email.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const name = parts.filter((p) => p !== email).join(" ").trim();
    rows.push({ name, email });
  });
  return rows;
}

// Inline editable text (name) with pencil toggle
const EditableName = ({ value, onSave, className = "", testid }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <input autoFocus value={val} onChange={(e) => setVal(e.target.value)} data-testid={`${testid}-input`}
          className="bg-[#0d0d0d] border border-white/20 rounded px-2 py-1 text-sm text-white w-40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#C41E24]" />
        <button data-testid={`${testid}-save`} onClick={async () => { await onSave(val.trim()); setEditing(false); }} className="text-[#22C55E] p-1"><Check size={15} /></button>
        <button onClick={() => { setVal(value); setEditing(false); }} className="text-white/40 p-1"><X size={15} /></button>
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-2 ${className}`}>
      {value}
      <button data-testid={`${testid}-edit`} onClick={() => setEditing(true)} className="text-white/30 hover:text-white transition-colors"><Pencil size={13} /></button>
    </span>
  );
};

// ---- Invite link result modal ----
const InviteResult = ({ invite, onClose }) => {
  if (!invite) return null;
  const rawUrl = invite.invite_url || "";
  const link = /^https?:\/\//i.test(rawUrl)
    ? rawUrl
    : (invite.setup_token ? `${window.location.origin}/coach-setup?token=${invite.setup_token}` : rawUrl);
  const copy = async (t) => {
    try {
      if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(t); }
      else {
        const ta = document.createElement("textarea"); ta.value = t; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
      }
      toast.success("Copied.");
    } catch { toast.error("Couldn't copy — select the link and copy manually."); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="invite-result-modal" onClick={onClose}>
      <div className="bg-[#111] border border-[#22C55E]/40 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3"><Link2 size={16} className="text-[#22C55E]" /><h4 className="font-subheadings text-sm font-bold uppercase tracking-widest text-white/85">Coach Access Link</h4></div>
        <p className="text-xs text-white/50 mb-4">Send this one-time link to <b className="text-white/80">{invite.email || "the coach"}</b>. They'll set their own name &amp; password, then get {invite.team_name ? <>the <b className="text-white/80">{invite.team_name}</b> team dashboard</> : "the club-wide dashboard"} — nothing else.</p>
        <div className="flex items-center gap-2 mb-3">
          <input data-testid="invite-link-field" readOnly value={link} onFocus={(e) => e.target.select()} className="flex-1 min-w-0 bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-white/90 text-xs font-mono" />
          <button data-testid="invite-link-copy" onClick={() => copy(link)} className="p-2 text-white/60 hover:text-white border border-white/10 rounded-md"><Copy size={15} /></button>
        </div>
        <button data-testid="invite-result-close" onClick={onClose} className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-md">Done</button>
      </div>
    </div>
  );
};

// ---- Coach invite form (used for club DOC + team) ----
const InviteForm = ({ token, clubName, teamName, onInvited, label }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/coach/coach-invites`,
        { club_name: clubName, team_name: teamName || "", name: name.trim(), email: email.trim().toLowerCase() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setName(""); setEmail(""); setOpen(false);
      onInvited(data);
    } catch (e) { toast.error(errMsg(e, "Couldn't create the invite.")); }
    finally { setBusy(false); }
  };
  if (!open) {
    return <button data-testid={`invite-open-${teamName || "doc"}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] hover:text-[#e8c65a] transition-colors"><UserPlus size={13} /> {label}</button>;
  }
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2 bg-[#0d0d0d] border border-white/10 rounded-lg p-2.5">
      <input data-testid={`invite-name-${teamName || "doc"}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Coach name" className="bg-[#141414] border border-white/10 rounded px-2 py-1.5 text-xs text-white w-32 focus-visible:outline-none" />
      <input data-testid={`invite-email-${teamName || "doc"}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="coach@email.com" className="bg-[#141414] border border-white/10 rounded px-2 py-1.5 text-xs text-white w-44 focus-visible:outline-none" />
      <button data-testid={`invite-submit-${teamName || "doc"}`} onClick={submit} disabled={busy} className="inline-flex items-center gap-1 bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded disabled:opacity-60">{busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Generate Link</button>
      <button onClick={() => setOpen(false)} className="text-white/40 p-1"><X size={14} /></button>
    </div>
  );
};

// ---- Revenue / trials summary panel ----
const RevenuePanel = ({ token }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/coach/revenue`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [token]);
  if (!data) return null;
  const cards = [
    { key: "trials", label: "Active Trials", value: data.active_trials, sub: data.trials_expiring_soon ? `${data.trials_expiring_soon} ending soon` : "on 7-day trial", icon: Crown, color: "#D4AF37" },
    { key: "conversions", label: "Conversions", value: data.conversions, sub: "trial → paid", icon: TrendingUp, color: "#22C55E" },
    { key: "members", label: "Paying Members", value: data.paying_members, sub: `${data.academy_members} Technical Track · ${data.elite_members} Elite`, icon: Users, color: "#C41E24" },
    { key: "mrr", label: "Monthly Revenue", value: `$${data.mrr}`, sub: "recurring (MRR)", icon: DollarSign, color: "#22C55E" },
  ];
  return (
    <div data-testid="revenue-panel" className="mb-7">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        {cards.map((c) => (
          <div key={c.key} data-testid={`revenue-${c.key}`} className="bg-[#141414] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2"><c.icon size={14} style={{ color: c.color }} /><span className="text-[10px] font-bold uppercase tracking-widest text-white/45">{c.label}</span></div>
            <div className="font-cover text-3xl leading-none" style={{ color: c.color }}>{c.value}</div>
            {c.sub && <div className="text-[11px] text-white/40 mt-1.5">{c.sub}</div>}
          </div>
        ))}
      </div>
      {data.trial_list?.length > 0 && (
        <div className="bg-[#0d0d0d] border border-white/10 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3"><Clock size={13} className="text-[#D4AF37]" /><span className="text-[11px] font-bold uppercase tracking-widest text-white/55">Active Trials — Convert Before They Expire</span></div>
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {data.trial_list.map((t) => (
              <div key={t.id} data-testid={`trial-item-${t.id}`} className="flex items-center gap-3 text-sm">
                <span className="flex-1 min-w-0 truncate text-white/85">{t.name}<span className="text-white/35"> · {t.club || "—"}{t.team ? ` / ${t.team}` : ""}</span></span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${t.days_left != null && t.days_left <= 2 ? "text-[#C41E24] border-[#C41E24]/40 bg-[#C41E24]/10" : "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10"}`}>{t.days_left === 0 ? "ended" : `${t.days_left}d left`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CoachChip = ({ c, token, onChange }) => (
  <span data-testid={`coach-chip-${c.id}`} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-white/5 border border-white/10 rounded-full pl-2.5 pr-1.5 py-1">
    <ShieldCheck size={11} className={c.activated ? "text-[#22C55E]" : "text-white/35"} />
    {c.name || c.email || "Invited"}{!c.activated && <span className="text-white/35 normal-case">(pending)</span>}
    <button data-testid={`coach-remove-${c.id}`} onClick={async () => {
      if (!window.confirm(`Remove coach access for ${c.email || c.name}?`)) return;
      try { await axios.delete(`${API}/coach/club-coaches/${c.id}`, { headers: { Authorization: `Bearer ${token}` } }); onChange(); toast.success("Coach removed."); }
      catch (e) { toast.error(errMsg(e, "Couldn't remove.")); }
    }} className="text-white/30 hover:text-[#C41E24] transition-colors"><X size={12} /></button>
  </span>
);

// ---- Team roster management modal ----
const TeamPanel = ({ token, club, team, onClose, onInvited, refresh }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [roster, setRoster] = useState([]);
  const [tab, setTab] = useState("roster");
  const [unassigned, setUnassigned] = useState([]);
  const [picked, setPicked] = useState({});
  const [q, setQ] = useState("");
  const [preRows, setPreRows] = useState([{ _id: crypto.randomUUID(), name: "", email: "" }]);
  const [busy, setBusy] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const fileRef = React.useRef(null);

  const onBulkFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setBulkText(String(reader.result || ""));
    reader.readAsText(f);
    e.target.value = "";
  };

  const applyBulk = () => {
    const parsed = parseRoster(bulkText);
    if (!parsed.length) { toast.error("No valid emails found. Use one 'Name, email' per line."); return; }
    setPreRows((rows) => {
      const existing = rows.filter((r) => r.email.trim());
      const seen = new Set(existing.map((r) => r.email.trim().toLowerCase()));
      const merged = [...existing];
      parsed.forEach((p) => { if (!seen.has(p.email.toLowerCase())) { seen.add(p.email.toLowerCase()); merged.push({ _id: crypto.randomUUID(), ...p }); } });
      return merged.length ? merged : [{ _id: crypto.randomUUID(), name: "", email: "" }];
    });
    setBulkText("");
    toast.success(`Loaded ${parsed.length} player${parsed.length > 1 ? "s" : ""} — review and add.`);
  };

  const loadRoster = useCallback(() => {
    axios.get(`${API}/coach/clubs/${club.id}/roster?team=${encodeURIComponent(team.name)}`, auth).then(({ data }) => setRoster(data)).catch(() => {});
  }, [club.id, team.name]);
  useEffect(() => { loadRoster(); }, [loadRoster]);

  const loadUnassigned = (query = "") => axios.get(`${API}/coach/unassigned-players?q=${encodeURIComponent(query)}`, auth).then(({ data }) => setUnassigned(data)).catch(() => {});
  useEffect(() => { if (tab === "assign") loadUnassigned(q); /* eslint-disable-next-line */ }, [tab]);

  const assign = async () => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    if (!ids.length) return;
    setBusy(true);
    try {
      await axios.post(`${API}/coach/assign-players`, { club_name: club.name, team: team.name, player_ids: ids }, auth);
      toast.success(`Added ${ids.length} player${ids.length > 1 ? "s" : ""}.`);
      setPicked({}); loadRoster(); loadUnassigned(q); refresh();
    } catch (e) { toast.error(errMsg(e, "Couldn't assign.")); } finally { setBusy(false); }
  };

  const preadd = async () => {
    const players = preRows.filter((r) => r.email.trim()).map(({ name, email }) => ({ name, email }));
    if (!players.length) { toast.error("Add at least one email."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/coach/roster/preadd`, { club_name: club.name, team: team.name, players }, auth);
      toast.success(`${data.count} player${data.count > 1 ? "s" : ""} added to the roster.`);
      setPreRows([{ _id: crypto.randomUUID(), name: "", email: "" }]); loadRoster(); refresh();
    } catch (e) { toast.error(errMsg(e, "Couldn't pre-add.")); } finally { setBusy(false); }
  };

  const removePlayer = async (id) => {
    try { await axios.post(`${API}/coach/roster/remove`, { player_id: id }, auth); loadRoster(); refresh(); toast.success("Removed from team."); }
    catch (e) { toast.error(errMsg(e, "Couldn't remove.")); }
  };

  const renamePlayer = async (id, name) => {
    try { await axios.put(`${API}/coach/players/${id}`, { display_name: name }, auth); loadRoster(); toast.success("Name updated."); }
    catch (e) { toast.error(errMsg(e, "Couldn't rename.")); }
  };

  const tabs = [["roster", "Roster"], ["assign", "Add Existing"], ["preadd", "Pre-add New"], ["coaches", "Team Coach"]];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="team-panel" onClick={onClose}>
      <div className="bg-[#111] border border-white/15 rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-white/10 sticky top-0 bg-[#111] z-10">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#C41E24]">{club.name}</div>
            <h3 className="font-headings text-2xl uppercase tracking-wide">{team.name}</h3>
          </div>
          <button data-testid="team-panel-close" onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={20} /></button>
        </div>
        <div className="flex gap-1 p-3 border-b border-white/10 flex-wrap">
          {tabs.map(([id, label]) => (
            <button key={id} data-testid={`team-tab-${id}`} onClick={() => setTab(id)}
              className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded transition-colors ${tab === id ? "bg-[#C41E24] text-white" : "bg-white/5 text-white/55 hover:text-white"}`}>{label}</button>
          ))}
        </div>
        <div className="p-5">
          {tab === "roster" && (
            <div className="space-y-2" data-testid="team-roster">
              {roster.length === 0 && <p className="text-sm text-white/40 py-4 text-center">No players on this team yet. Use "Add Existing" or "Pre-add New".</p>}
              {roster.map((p) => (
                <div key={p.id} data-testid={`roster-row-${p.id}`} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/10 rounded-lg px-3 py-2.5">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${p.active ? "bg-[#22C55E]" : "bg-white/25"}`} title={p.active ? "Active" : "Pending signup"} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                      <EditableName value={p.name} onSave={(v) => renamePlayer(p.id, v)} testid={`roster-name-${p.id}`} />
                      {p.jersey_number ? <span className="text-[10px] font-bold text-white/50">#{p.jersey_number}</span> : null}
                      {p.is_trial && p.trial_days_left != null && (
                        <span data-testid={`roster-trial-${p.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-full px-2 py-0.5">
                          <Crown size={10} /> {p.trial_days_left === 0 ? "Trial ended" : `Trial · ${p.trial_days_left}d left`}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/40 truncate">{p.email} · {p.active ? p.tier : "not signed up"}</div>
                  </div>
                  <button data-testid={`roster-remove-${p.id}`} onClick={() => removePlayer(p.id)} className="text-white/30 hover:text-[#C41E24] transition-colors p-1"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
          {tab === "assign" && (
            <div data-testid="team-assign">
              <div className="flex gap-2 mb-3">
                <input data-testid="assign-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUnassigned(q)} placeholder="Search unassigned players…" className="flex-1 bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none" />
                <button onClick={() => loadUnassigned(q)} className="px-3 py-2 border border-white/15 rounded-md text-xs text-white/70 hover:text-white">Search</button>
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto mb-4">
                {unassigned.length === 0 && <p className="text-sm text-white/40 py-3 text-center">No unassigned active players found.</p>}
                {unassigned.map((p) => (
                  <label key={p.id} data-testid={`assign-option-${p.id}`} className="flex items-center gap-3 bg-[#0d0d0d] border border-white/10 rounded-lg px-3 py-2 cursor-pointer hover:border-white/25">
                    <input type="checkbox" checked={!!picked[p.id]} onChange={(e) => setPicked((s) => ({ ...s, [p.id]: e.target.checked }))} className="accent-[#C41E24]" />
                    <div className="flex-1 min-w-0"><div className="text-sm truncate">{p.name}</div><div className="text-[11px] text-white/40 truncate">{p.email}</div></div>
                    <span className="text-[10px] uppercase tracking-wider text-white/40">{p.tier}</span>
                  </label>
                ))}
              </div>
              <button data-testid="assign-submit" onClick={assign} disabled={busy} className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-md disabled:opacity-50">{busy ? "Adding…" : `Add ${Object.values(picked).filter(Boolean).length} to ${team.name}`}</button>
            </div>
          )}
          {tab === "preadd" && (
            <div data-testid="team-preadd">
              <p className="text-xs text-white/50 mb-3">Add players who haven't signed up yet — they'll be auto-linked to this team when they register with the same email.</p>

              {/* Bulk CSV / paste import */}
              <div className="bg-[#0d0d0d] border border-white/10 rounded-lg p-3 mb-4" data-testid="bulk-import">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-white/60"><ClipboardList size={13} className="text-[#D4AF37]" /> Bulk Import</span>
                  <button data-testid="bulk-upload-btn" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 rounded px-2.5 py-1.5"><Upload size={12} /> Upload CSV</button>
                  <input ref={fileRef} type="file" accept=".csv,.txt" onChange={onBulkFile} className="hidden" data-testid="bulk-file-input" />
                </div>
                <textarea data-testid="bulk-textarea" value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={4}
                  placeholder={"Paste your roster — one per line:\nMason Carter, mason@club.com\nliam@club.com"}
                  className="w-full bg-[#141414] border border-white/10 rounded-md px-3 py-2 text-xs text-white font-mono focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#C41E24] resize-y" />
                <div className="flex items-center justify-between gap-2 mt-2">
                  <span className="text-[10px] text-white/35">Accepts "Name, email" or just email · commas, tabs or semicolons</span>
                  <button data-testid="bulk-parse-btn" onClick={applyBulk} disabled={!bulkText.trim()} className="inline-flex items-center gap-1 bg-[#D4AF37] text-black text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded disabled:opacity-40"><Plus size={12} /> Load Into List</button>
                </div>
              </div>

              <div className="space-y-2 mb-3">
                {preRows.map((r, i) => (
                  <div key={r._id} className="flex gap-2">
                    <input data-testid={`preadd-name-${i}`} value={r.name} onChange={(e) => setPreRows((rows) => rows.map((x) => x._id === r._id ? { ...x, name: e.target.value } : x))} placeholder="Name" className="w-1/3 bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none" />
                    <input data-testid={`preadd-email-${i}`} type="email" value={r.email} onChange={(e) => setPreRows((rows) => rows.map((x) => x._id === r._id ? { ...x, email: e.target.value } : x))} placeholder="email@club.com" className="flex-1 bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none" />
                    {preRows.length > 1 && <button onClick={() => setPreRows((rows) => rows.filter((x) => x._id !== r._id))} className="text-white/30 hover:text-[#C41E24] p-1"><X size={16} /></button>}
                  </div>
                ))}
              </div>
              <button data-testid="preadd-addrow" onClick={() => setPreRows((r) => [...r, { _id: crypto.randomUUID(), name: "", email: "" }])} className="text-[11px] font-bold uppercase tracking-wider text-white/50 hover:text-white mb-4 inline-flex items-center gap-1"><Plus size={13} /> Add another</button>
              <button data-testid="preadd-submit" onClick={preadd} disabled={busy} className="w-full bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-md disabled:opacity-50">{busy ? "Adding…" : `Add ${preRows.filter((r) => r.email.trim()).length || ""} to Roster`.replace("  ", " ")}</button>
            </div>
          )}
          {tab === "coaches" && (
            <div data-testid="team-coaches">
              <p className="text-xs text-white/50 mb-3">Invite a coach who can view ONLY the <b className="text-white/80">{team.name}</b> dashboard.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {(team.coaches || []).length === 0 && <span className="text-sm text-white/40">No team coaches yet.</span>}
                {(team.coaches || []).map((c) => <CoachChip key={c.id} c={c} token={token} onChange={refresh} />)}
              </div>
              <InviteForm token={token} clubName={club.name} teamName={team.name} label="Invite Team Coach" onInvited={onInvited} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function ClubManager({ token }) {
  const navigate = useNavigate();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [clubs, setClubs] = useState([]);
  const [newClub, setNewClub] = useState("");
  const [expanded, setExpanded] = useState({});
  const [newTeam, setNewTeam] = useState({});
  const [manage, setManage] = useState(null); // {club, team}
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showClubs, setShowClubs] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/coach/clubs`, auth).then(({ data }) => setClubs(data)).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); }, [load]);

  const addClub = async () => {
    if (!newClub.trim()) return;
    try { await axios.post(`${API}/coach/clubs`, { name: newClub.trim() }, auth); setNewClub(""); load(); toast.success("Club created."); }
    catch (e) { toast.error(errMsg(e, "Couldn't create club.")); }
  };
  const renameClub = async (id, name) => { try { await axios.put(`${API}/coach/clubs/${id}`, { name }, auth); load(); toast.success("Club renamed."); } catch (e) { toast.error(errMsg(e, "Couldn't rename.")); } };
  const deleteClub = async (c) => { if (!window.confirm(`Delete club "${c.name}"? Players keep their data but lose the club tag.`)) return; try { await axios.delete(`${API}/coach/clubs/${c.id}`, auth); load(); toast.success("Club deleted."); } catch (e) { toast.error(errMsg(e, "Couldn't delete.")); } };
  const addTeam = async (cid) => { const n = (newTeam[cid] || "").trim(); if (!n) return; try { await axios.post(`${API}/coach/clubs/${cid}/teams`, { name: n }, auth); setNewTeam((s) => ({ ...s, [cid]: "" })); load(); toast.success("Team added."); } catch (e) { toast.error(errMsg(e, "Couldn't add team.")); } };
  const renameTeam = async (cid, tid, name) => { try { await axios.put(`${API}/coach/clubs/${cid}/teams/${tid}`, { name }, auth); load(); toast.success("Team renamed."); } catch (e) { toast.error(errMsg(e, "Couldn't rename.")); } };
  const deleteTeam = async (cid, t) => { if (!window.confirm(`Delete team "${t.name}"?`)) return; try { await axios.delete(`${API}/coach/clubs/${cid}/teams/${t.id}`, auth); load(); toast.success("Team deleted."); } catch (e) { toast.error(errMsg(e, "Couldn't delete.")); } };
  const copyCode = (code) => { navigator.clipboard.writeText(code); toast.success(`Code ${code} copied.`); };
  const copyRedeemLink = (code) => { const url = `${window.location.origin}/redeem?code=${encodeURIComponent(code)}`; navigator.clipboard.writeText(url); toast.success("Redeem link copied — share it with parents."); };
  const regenerateCode = async (cid, t) => { if (!window.confirm(`Generate a NEW code for "${t.name}"? The current code will stop working immediately.`)) return; try { const { data } = await axios.post(`${API}/coach/clubs/${cid}/teams/${t.id}/regenerate-code`, {}, auth); load(); toast.success(`New code: ${data.code}`); } catch (e) { toast.error(errMsg(e, "Couldn't regenerate.")); } };

  const onInvited = (data) => { setInvite(data); load(); };
  const viewDashboard = (clubName, teamName) => {
    const p = new URLSearchParams({ club: clubName });
    if (teamName) p.set("team", teamName);
    navigate(`/coach/club-view?${p.toString()}`);
  };

  return (
    <div data-testid="club-manager">
      <div className="flex items-center gap-2 mb-1">
        <Building2 size={18} className="text-[#C41E24]" />
        <h2 className="font-headings text-2xl uppercase tracking-wide">Club Manager</h2>
      </div>
      <p className="text-sm text-white/45 mb-6 max-w-2xl">Build your multi-club platform: create clubs and teams, assign players, and invite coaches with access limited to their own team's live dashboard.</p>

      <RevenuePanel token={token} />

      {/* Add club */}
      <div className="flex gap-2 mb-6">
        <input data-testid="new-club-input" value={newClub} onChange={(e) => setNewClub(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addClub()} placeholder="New club name (e.g. Peoria FC United)"
          className="flex-1 max-w-sm bg-[#0d0d0d] border border-white/15 rounded-md px-3 py-2.5 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
        <button data-testid="add-club-btn" onClick={addClub} className="inline-flex items-center gap-1.5 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-md"><Plus size={15} /> Add Club</button>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <span className="font-subheadings text-xs font-bold uppercase tracking-widest text-white/60">Your Clubs</span>
        <button data-testid="toggle-clubs-accordion" onClick={() => setShowClubs((v) => !v)}
          className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3.5 py-2.5 rounded-md border border-white/15 text-white/70 hover:text-white hover:border-white/40 transition-colors">
          <ChevronDown size={15} className={`transition-transform ${showClubs ? "rotate-180" : ""}`} /> {showClubs ? "Hide" : `Show All (${clubs.length})`}
        </button>
      </div>

      {showClubs && (loading ? <div className="text-white/40 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div> : (
        <div className="space-y-3">
          {clubs.length === 0 && <p data-testid="clubs-empty" className="text-sm text-white/40">No clubs yet. Create your first club above.</p>}
          {clubs.map((c) => {
            const open = !!expanded[c.id];
            return (
              <div key={c.id} data-testid={`club-card-${c.id}`} className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <button data-testid={`club-toggle-${c.id}`} onClick={() => setExpanded((s) => ({ ...s, [c.id]: !open }))} className="text-white/40 hover:text-white">{open ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</button>
                  <Building2 size={18} className="text-[#C41E24] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-subheadings font-bold uppercase tracking-wide text-sm"><EditableName value={c.name} onSave={(v) => renameClub(c.id, v)} testid={`club-name-${c.id}`} /></div>
                    <div className="text-[11px] text-white/40">{c.teams.length} team{c.teams.length !== 1 ? "s" : ""} · {c.player_count} player{c.player_count !== 1 ? "s" : ""}</div>
                  </div>
                  <button data-testid={`club-view-${c.id}`} onClick={() => viewDashboard(c.name, "")} className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider bg-[#C41E24]/15 hover:bg-[#C41E24]/25 border border-[#C41E24]/40 text-white px-3 py-1.5 rounded"><LineChart size={13} /> View Dashboard</button>
                  <button data-testid={`club-delete-${c.id}`} onClick={() => deleteClub(c)} className="text-white/30 hover:text-[#C41E24] transition-colors p-1"><Trash2 size={16} /></button>
                </div>

                {open && (
                  <div className="px-4 pb-4 border-t border-white/10 pt-4 space-y-4">
                    {/* Club-wide DOC coaches */}
                    <div className="bg-[#0d0d0d] border border-white/10 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2"><Crown size={13} className="text-[#D4AF37]" /><span className="text-[11px] font-bold uppercase tracking-widest text-white/60">Club-Wide Coaches (DOC)</span></div>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(c.club_coaches || []).length === 0 && <span className="text-xs text-white/40">None yet.</span>}
                        {(c.club_coaches || []).map((cc) => <CoachChip key={cc.id} c={cc} token={token} onChange={load} />)}
                      </div>
                      <InviteForm token={token} clubName={c.name} teamName="" label="Invite Club Coach (sees all teams)" onInvited={onInvited} />
                    </div>

                    {/* Teams */}
                    <div>
                      <div className="flex items-center gap-2 mb-2"><Users size={13} className="text-white/50" /><span className="text-[11px] font-bold uppercase tracking-widest text-white/60">Teams</span></div>
                      <div className="space-y-2 mb-3">
                        {c.teams.length === 0 && <p className="text-xs text-white/40">No teams yet — add one below (e.g. "U9 Boys").</p>}
                        {c.teams.map((t) => (
                          <div key={t.id} data-testid={`team-row-${t.id}`} className="bg-[#0d0d0d] border border-white/10 rounded-lg px-3 py-2.5">
                            <div className="flex items-center gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold"><EditableName value={t.name} onSave={(v) => renameTeam(c.id, t.id, v)} testid={`team-name-${t.id}`} /></div>
                                <div className="text-[11px] text-white/40">{t.active_count}/{t.player_count} active · {(t.coaches || []).length} coach{(t.coaches || []).length !== 1 ? "es" : ""}</div>
                              </div>
                              <button data-testid={`team-view-${t.id}`} onClick={() => viewDashboard(c.name, t.name)} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#C41E24]/15 hover:bg-[#C41E24]/25 border border-[#C41E24]/40 text-white px-2.5 py-1.5 rounded"><LineChart size={13} /> View</button>
                              <button data-testid={`team-manage-${t.id}`} onClick={() => setManage({ club: c, team: t })} className="text-[10px] font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 border border-white/15 text-white/80 px-3 py-1.5 rounded">Manage</button>
                              <button data-testid={`team-delete-${t.id}`} onClick={() => deleteTeam(c.id, t)} className="text-white/30 hover:text-[#C41E24] transition-colors p-1"><Trash2 size={15} /></button>
                            </div>
                            {/* Parent invite code -> free 7-day Elite trial joined to this team */}
                            <div className="flex flex-wrap items-center gap-2 mt-2.5 pt-2.5 border-t border-white/5">
                              <Ticket size={13} className="text-[#D4AF37] shrink-0" />
                              <span className="text-[10px] font-bold uppercase tracking-widest text-white/40">Team Code</span>
                              <code data-testid={`team-code-${t.id}`} className="font-mono text-sm font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded px-2 py-0.5 tracking-wider">{t.code || "—"}</code>
                              <button data-testid={`team-code-copy-${t.id}`} onClick={() => copyCode(t.code)} title="Copy code" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Copy size={12} /> Code</button>
                              <button data-testid={`team-code-link-${t.id}`} onClick={() => copyRedeemLink(t.code)} title="Copy shareable redeem link" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/55 hover:text-white border border-white/15 rounded px-2 py-1"><Link2 size={12} /> Link</button>
                              <button data-testid={`team-code-poster-${t.id}`} onClick={() => openTrialPoster({ code: t.code, teamName: t.name })} title="Printable QR flyer for this team" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black bg-[#D4AF37] hover:bg-[#e8c65a] rounded px-2 py-1"><QrCode size={12} /> Poster</button>
                              <button data-testid={`team-code-regen-${t.id}`} onClick={() => regenerateCode(c.id, t)} title="Generate a new code (old one stops working)" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/45 hover:text-[#C41E24] px-1.5 py-1"><RefreshCw size={12} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <input data-testid={`new-team-input-${c.id}`} value={newTeam[c.id] || ""} onChange={(e) => setNewTeam((s) => ({ ...s, [c.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && addTeam(c.id)} placeholder="New team (e.g. U9 Boys)"
                          className="flex-1 max-w-xs bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none" />
                        <button data-testid={`add-team-btn-${c.id}`} onClick={() => addTeam(c.id)} className="inline-flex items-center gap-1 border border-white/15 hover:border-white/40 text-white/80 text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md"><Plus size={14} /> Add Team</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {manage && (() => {
        const freshClub = clubs.find((x) => x.id === manage.club.id) || manage.club;
        const freshTeam = (freshClub.teams || []).find((x) => x.id === manage.team.id) || manage.team;
        return <TeamPanel token={token} club={freshClub} team={freshTeam} onClose={() => setManage(null)} onInvited={onInvited} refresh={load} />;
      })()}
      {invite && <InviteResult invite={invite} onClose={() => setInvite(null)} />}
    </div>
  );
}
